import React, { useEffect, useState } from 'react';
import { Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { DeleteConfirmation, InputText } from '../../common/components';
import { addStatusToast } from '../../common/components/StatusToast/StatusToastSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  createIdentityVerificationLinkAsync,
  deletePersonalIdentityAsync,
  getPersonalIdentityDataAsync,
  savePersonalIdentityAsync,
  selectPersonalIdentityData,
  selectPersonalIdentityLoadingStatus,
} from './identityUsersSlice';
import { LOADING_STATUS } from '../../common/constants';

interface PersonalIdentityTabProps {
  accountId: string;
  isActive: boolean;
}

function PersonalIdentityTab({
  accountId,
  isActive,
}: PersonalIdentityTabProps) {
  const dispatch = useAppDispatch();
  const personalIdentityData = useAppSelector(selectPersonalIdentityData);
  const personalIdentityLoadingStatus = useAppSelector(
    selectPersonalIdentityLoadingStatus,
  );
  const [personalIdentityCode, setPersonalIdentityCode] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [verificationLink, setVerificationLink] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState<boolean>(false);
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && !hasLoaded && accountId) {
      dispatch(getPersonalIdentityDataAsync(accountId));
      setHasLoaded(true);
    }
  }, [isActive, hasLoaded, accountId, dispatch]);

  // Reset hasLoaded and verificationLink when accountId changes
  useEffect(() => {
    setHasLoaded(false);
    setVerificationLink('');
  }, [accountId]);

  const handleSave = async () => {
    if (!personalIdentityCode || personalIdentityCode.trim() === '') {
      dispatch(
        addStatusToast({
          Title: 'Tallennus epäonnistui',
          IsFailed: true,
          Message: 'Henkilötunnus puuttuu',
        }),
      );
      return;
    }
    try {
      await dispatch(
        savePersonalIdentityAsync({
          accountId: accountId || '',
          personalIdentityCode: personalIdentityCode.trim(),
        }),
      );
      dispatch(
        addStatusToast({
          Title: 'Henkilötunnus tallennettu',
          IsFailed: false,
          Message: 'Henkilötunnus tallennettu',
        }),
      );
      setPersonalIdentityCode('');
      dispatch(getPersonalIdentityDataAsync(accountId));
    } catch (err) {
      dispatch(
        addStatusToast({
          Title: 'Tallennus epäonnistui',
          IsFailed: true,
          Message: 'Tallennus epäonnistui',
        }),
      );
    }
  };

  const handleGenerateLink = async () => {
    setIsGeneratingLink(true);
    try {
      const result = await dispatch(
        createIdentityVerificationLinkAsync({
          accountId,
          returnUrl: '/Account/IdentityVerified',
        }),
      ).unwrap();
      setVerificationLink(result);
      dispatch(
        addStatusToast({
          Title: 'Linkki luotu',
          IsFailed: false,
          Message: 'Vahvan tunnistautumisen linkki luotu onnistuneesti',
        }),
      );
    } catch (err) {
      dispatch(
        addStatusToast({
          Title: 'Linkin luonti epäonnistui',
          IsFailed: true,
          Message: 'Linkin luonti vahvaan tunnistautumiseen epäonnistui',
        }),
      );
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(verificationLink);
      dispatch(
        addStatusToast({
          Title: 'Kopioitu',
          IsFailed: false,
          Message: 'Linkki kopioitu leikepöydälle',
        }),
      );
    } catch (err) {
      dispatch(
        addStatusToast({
          Title: 'Kopiointi epäonnistui',
          IsFailed: true,
          Message: 'Linkin kopiointi epäonnistui',
        }),
      );
    }
  };

  const handleDeletePersonalIdentity = async () => {
    setIsDeleting(true);
    setIsDeleteConfirmationVisible(false);
    try {
      await dispatch(deletePersonalIdentityAsync(accountId)).unwrap();
      dispatch(
        addStatusToast({
          Title: 'Tiedot poistettu',
          IsFailed: false,
          Message: 'Vahvan tunnistautumisen tiedot poistettu onnistuneesti',
        }),
      );
      dispatch(getPersonalIdentityDataAsync(accountId));
    } catch (err) {
      dispatch(
        addStatusToast({
          Title: 'Poisto epäonnistui',
          IsFailed: true,
          Message: 'Vahvan tunnistautumisen tietojen poistaminen epäonnistui',
        }),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Show loading state when data hasn't been loaded for the current user yet
  const isLoading =
    !hasLoaded || personalIdentityLoadingStatus === LOADING_STATUS.LOADING;

  let statusContent;
  if (isLoading) {
    statusContent = <Spinner animation="border" size="sm" />;
  } else if (personalIdentityData?.hasPersonalIdentityCode) {
    statusContent = <Badge bg="success">Tallennettu</Badge>;
  } else {
    statusContent = <Badge bg="secondary">Puuttuu</Badge>;
  }

  let buttonContent;
  if (isGeneratingLink) {
    buttonContent = (
      <>
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
          className="me-8"
        />
        Luodaan...
      </>
    );
  } else if (verificationLink) {
    buttonContent = 'Luo uusi linkki';
  } else {
    buttonContent = 'Luo linkki';
  }

  return (
    <div className="mb-24">
      <div className="d-flex justify-content-between align-items-center mb-16">
        <h2 className="h5 h-md-4 mb-0">Henkilötunnus</h2>
        {!isLoading && personalIdentityData?.hasPersonalIdentityCode && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setIsDeleteConfirmationVisible(true)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-8"
                />
                Poistetaan...
              </>
            ) : (
              'Poista tiedot'
            )}
          </Button>
        )}
      </div>
      <div className="d-flex align-items-center mb-8">
        <span className="me-12">Tila:</span>
        {statusContent}
      </div>
      {!isLoading && personalIdentityData?.issuedAt && (
        <div>
          <div className="text-muted mb-12">
            Vahva tunnistautuminen tehty:{' '}
            {new Date(personalIdentityData.issuedAt).toLocaleString('fi-FI')}
          </div>
          <div className="text-muted mb-12">
            Käytetty tunnistautumistapa :{' '}
            {personalIdentityData?.bankTupasId || 'Ei tiedossa'}
          </div>
        </div>
      )}
      {!isLoading && personalIdentityData?.updatedAt && (
        <div className="text-muted mb-12">
          Tietoja muokattu:{' '}
          {new Date(personalIdentityData.updatedAt).toLocaleString('fi-FI')}
        </div>
      )}
      {!isLoading && !personalIdentityData?.hasPersonalIdentityCode && (
        <div className="d-flex align-items-end gap-12">
          <div className="flex-grow-1">
            <InputText
              HandleChange={e => setPersonalIdentityCode(e.target.value)}
              Id="personalIdentityCode"
              Name="personalIdentityCode"
              Value={personalIdentityCode}
              IsRequired={false}
              Label="Lisää henkilötunnus"
            />
          </div>
          <div className="mb-24">
            <Button variant="primary" onClick={handleSave}>
              Tallenna
            </Button>
          </div>
        </div>
      )}

      {/* Strong authentication link generation */}
      {!isLoading && (
        <div className="mt-24">
          <h3 className="h6 mb-16">Vahvan tunnistautumisen linkki</h3>
          <div className="mb-12">
            <Button
              variant="outline-primary"
              onClick={handleGenerateLink}
              disabled={isGeneratingLink}
            >
              {buttonContent}
            </Button>
          </div>
          {verificationLink && (
            <InputGroup>
              <Form.Control
                type="text"
                value={verificationLink}
                readOnly
                disabled
              />
              <Button variant="outline-secondary" onClick={handleCopyLink}>
                <Icon Name="clipboard" />
              </Button>
            </InputGroup>
          )}
        </div>
      )}

      <DeleteConfirmation
        ConfirmDelete={handleDeletePersonalIdentity}
        HideModal={() => setIsDeleteConfirmationVisible(false)}
        Id={accountId}
        Message="Haluatko varmasti poistaa kaikki vahvassa tunnistautumisessa kerätyt tiedot käyttäjältä?"
        ShowModal={isDeleteConfirmationVisible}
        Title="Vahvan tunnistautumisen tietojen poisto"
      />
    </div>
  );
}

export default PersonalIdentityTab;
